/**
 * Правая панель просмотра — первый оверлей продукта.
 *
 * До неё любой просмотр детали был полным переходом: карта уносила со страницы,
 * и человек терял и позицию, и разобранные фильтры, и кадр карты, ради которого
 * он сюда пришёл. Панель отвечает на вопрос «что это за единица», не отбирая
 * контекст; полный уровень остаётся одним нажатием ниже и ссылкой, которую
 * можно скопировать.
 *
 * Что закреплено формой:
 *
 * * **это превью, а не уровень.** Подзаголовок говорит это вслух, а нижняя
 *   строка всегда предлагает переход: панель не притворяется карточкой;
 * * **Esc закрывает, фокус возвращается туда, откуда пришёл.** Иначе клавиатура
 *   после закрытия оказывается в начале документа;
 * * **фокус заперт внутри панели, пока она открыта.** Панель модальная:
 *   `aria-modal`, и Tab по кругу. Открытая панель, из которой Tab уводит в
 *   спрятанный за ней экран, хуже, чем её отсутствие;
 * * **разметка уходит в портал.** Внутри `overflow: hidden` рамки карты панель
 *   иначе обрезалась бы своим же контейнером.
 *
 * Компонент ничего не знает про предмет: он показывает то, что ему дали, и не
 * строит ссылок.
 */

import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import styles from "./detaildrawer.module.css";

const m = defineMessages({
  kicker: { id: "common.kit.drawer.kicker", defaultMessage: "Превью: полный уровень — по ссылке ниже", description: "Подпись под заголовком боковой панели-превью: это краткий просмотр, полная страница — по ссылке внизу панели" },
  close: { id: "common.kit.drawer.close", defaultMessage: "Закрыть превью", description: "aria-label кнопки-крестика боковой панели-превью" },
});

/** Что внутри панели может получить фокус. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

export function DetailDrawer({
  open,
  title,
  kicker,
  children,
  footer,
  onClose,
}: {
  open: boolean;
  /** Имя единицы: «Контур 0102-03». */
  title: ReactNode;
  /** Чем эта панель НЕ является. Пустая строка убирает подпись. */
  kicker?: string;
  children: ReactNode;
  /** Переход на полный уровень. Собирает вызывающий — вместе с контекстом. */
  footer?: ReactNode;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const t = useT();
  const kickerText = kicker ?? t(m.kicker);

  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    returnTo.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== "Tab" || !panel) return;
      // Скрытое из круга исключается по объявленному стилю, а не по
      // `offsetParent`: у него нет значения без раскладки (jsdom, печать), и
      // тогда круг оказывался бы пустым там, где он на самом деле полон.
      const nodes = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (node) => {
          if (node.hasAttribute("hidden")) return false;
          const style = window.getComputedStyle(node);
          return style.display !== "none" && style.visibility !== "hidden";
        },
      );
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      // Пустая панель всё равно держит фокус на себе: уводить его наружу из
      // модального слоя нельзя ни в одном из направлений.
      if (!first || !last) {
        event.preventDefault();
        panel.focus();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      returnTo.current?.focus();
    };
  }, [open, close]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      // Клик мимо панели закрывает её — но только сам фон, а не всплывшее
      // событие изнутри.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className={styles.head}>
          <h2 className={styles.title} id={titleId}>
            {title}
            {kickerText ? <span className={styles.kicker}>{kickerText}</span> : null}
          </h2>
          <button
            type="button"
            className={styles.close}
            onClick={close}
            aria-label={t(m.close)}
          >
            <X size={18} strokeWidth={1.75} aria-hidden />
          </button>
        </div>

        <div className={styles.body}>{children}</div>

        {footer ? <div className={styles.foot}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
